/**
 * Support feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * All hooks are thin wrappers over orval-generated client
 * (`@shared/api/generated/support/support`).
 * They add refetchInterval for queries and cache invalidation for mutations.
 *
 * For new support endpoints always extend this file, never `@api/hooks.ts`.
 */
import type { SupportListTicketsParams } from "@shared/api/generated/model";
import {
  getSupportListMessagesQueryKey,
  getSupportListTicketsQueryKey,
  useSupportCreateTicket as useGenCreateTicket,
  useSupportGetTicket as useGenGetTicket,
  useSupportListMessages as useGenListMessages,
  useSupportListTickets as useGenListTickets,
  useSupportMarkMessageRead as useGenMarkMessageRead,
  useSupportMarkTicketRead as useGenMarkTicketRead,
  useSupportSendMessage as useGenSendMessage,
  useSupportUpdateTicket as useGenUpdateTicket,
} from "@shared/api/generated/support/support";
import { useQueryClient } from "@tanstack/react-query";

export type {
  SupportListTicketsParams,
  SupportMessage,
  SupportTicket,
} from "@shared/api/generated/model";

// ── Queries ─────────────────────────────────────────────────────────────────────

export const useSupportTickets = (params?: SupportListTicketsParams) =>
  useGenListTickets(params, { query: { refetchInterval: 15_000 } });

export const useSupportMessages = (ticketId?: string) =>
  useGenListMessages(ticketId ?? "", {
    query: { refetchInterval: 10_000, enabled: !!ticketId },
  });

export const useSupportTicket = (id?: string) =>
  useGenGetTicket(id ?? "", {
    query: { enabled: !!id, refetchInterval: 15_000 },
  });

// ── Mutations (with cache invalidation) ─────────────────────────────────────────

export const useCreateSupportTicket = () => {
  const qc = useQueryClient();
  return useGenCreateTicket({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getSupportListTicketsQueryKey() });
      },
    },
  });
};

export const useSendSupportMessage = () => {
  const qc = useQueryClient();
  return useGenSendMessage({
    mutation: {
      onSuccess: (_data, variables) => {
        qc.invalidateQueries({
          queryKey: getSupportListMessagesQueryKey(variables.id),
        });
        qc.invalidateQueries({ queryKey: getSupportListTicketsQueryKey() });
      },
    },
  });
};

export const useUpdateSupportTicket = () => {
  const qc = useQueryClient();
  return useGenUpdateTicket({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getSupportListTicketsQueryKey() });
      },
    },
  });
};

export const useMarkSupportRead = () => {
  const qc = useQueryClient();
  return useGenMarkTicketRead({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getSupportListTicketsQueryKey() });
      },
    },
  });
};

export const useMarkSupportMessageRead = (ticketId?: string) => {
  const qc = useQueryClient();
  return useGenMarkMessageRead({
    mutation: {
      onSuccess: () => {
        if (ticketId) {
          qc.invalidateQueries({
            queryKey: getSupportListMessagesQueryKey(ticketId),
          });
        }
        qc.invalidateQueries({ queryKey: getSupportListTicketsQueryKey() });
      },
    },
  });
};
